import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { transactionLineItems } from '../../util/api';
import * as log from '../../util/log';
import { denormalisedResponseEntities } from '../../util/data';
import { getLastCounterpartyMessageFromParts } from '../../util/transactionMessages';
import {
  bookingTimeUnits,
  findNextBoundary,
  getStartOf,
  monthIdString,
  stringifyDateToISO8601,
} from '../../util/dates';
import {
  hasPermissionToInitiateTransactions,
  hasPermissionToViewData,
  isUserAuthorized,
} from '../../util/userHelpers';
import { boostApi } from '../../ducks/boostApi';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import {
  getProcess,
  isBookingProcessAlias,
  isNegotiationProcessAlias,
  resolveLatestProcessName,
  OFFER,
} from '../../transactions/transaction';
import { fetchCurrentUser, setCurrentUserHasOrders } from '../../ducks/user.duck';
import { fetchPickupCodeThunk } from '../../ducks/pickup.duck';

const PICKUP_CODE_LAST_TRANSITIONS = [
  'transition/confirm-payment',
  'transition/share-pickup-address',
  'transition/customer-activate-pickup-code',
  'transition/capture-at-inspection-expire',
];

const { UUID } = sdkTypes;
const MINUTE_IN_MS = 1000 * 60;

// Day-based time slots queries are cached for 1 minute.
const removeOutdatedDateData = timeSlotsForDate => {
  const now = new Date().getTime();
  const minuteAgo = now - MINUTE_IN_MS;
  return Object.fromEntries(
    Object.entries(timeSlotsForDate).filter(([dateId, data]) => {
      return data.fetchedAt && data.fetchedAt > minuteAgo;
    })
  );
};

// ================ Async Thunks ================ //

//////////////////
// Show Listing //
//////////////////
const showListingPayloadCreator = ({ listingId, config, isOwn = false }, thunkAPI) => {
  const { dispatch, rejectWithValue, extra: sdk } = thunkAPI;
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  // Current user entity is fetched in a bit lazy fashion, since it's not tied to returned Promise chain.
  const fetchCurrentUserOptions = {
    updateHasListings: false,
    updateNotifications: false,
  };
  dispatch(fetchCurrentUser(fetchCurrentUserOptions));

  const params = {
    id: listingId,
    include: ['author', 'author.profileImage', 'images', 'currentStock'],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'profile.metadata'],
    'fields.image': [
      // Scaled variants for large images
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',

      // Cropped variants for listing thumbnail images
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      `variants.${variantPrefix}-4x`,
      `variants.${variantPrefix}-6x`,

      // Social media
      'variants.facebook',
      'variants.twitter',

      // Avatars
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
  };

  const show = isOwn ? sdk.ownListings.show(params) : sdk.listings.show(params);

  return show
    .then(data => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };
      dispatch(addMarketplaceEntities(data, sanitizeConfig));
      return data;
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const showListingThunk = createAsyncThunk(
  'ListingPage/showListing',
  showListingPayloadCreator
);
// Backward compatible wrapper for the thunk
export const showListing = (listingId, config, isOwn = false) => (dispatch, getState, sdk) => {
  return dispatch(showListingThunk({ listingId, config, isOwn })).unwrap();
};

///////////////////
// Fetch Reviews //
///////////////////
export const fetchReviewsThunk = createAsyncThunk(
  'ListingPage/fetchReviews',
  ({ listingId }, { rejectWithValue, extra: sdk }) => {
    return sdk.reviews
      .query({
        listing_id: listingId,
        state: 'public',
        include: ['author', 'author.profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      })
      .then(response => {
        return denormalisedResponseEntities(response);
      })
      .catch(e => {
        return rejectWithValue(storableError(e));
      });
  }
);

export const fetchReviews = listingId => (dispatch, getState, sdk) => {
  return dispatch(fetchReviewsThunk({ listingId })).unwrap();
};

/////////////////////////////
// Fetch Seller Reviews    //
// (all reviews for a user //
// across all listings)    //
/////////////////////////////

export const fetchSellerReviewsThunk = createAsyncThunk(
  'ListingPage/fetchSellerReviews',
  ({ userId }, { rejectWithValue, extra: sdk }) => {
    return sdk.reviews
      .query({
        subject_id: userId,
        state: 'public',
        include: ['author', 'author.profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      })
      .then(response => denormalisedResponseEntities(response))
      .catch(e => rejectWithValue(storableError(e)));
  }
);

export const fetchSellerReviews = userId => (dispatch, getState, sdk) => {
  return dispatch(fetchSellerReviewsThunk({ userId })).unwrap();
};

//////////////////////
// Fetch Time Slots //
//////////////////////

const timeSlotsRequest = createAsyncThunk(
  'ListingPage/timeSlotsRequest',
  (params, { extra: sdk }) => {
    return sdk.timeslots.query(params).then(response => {
      return denormalisedResponseEntities(response);
    });
  }
);

const fetchTimeSlotsPayloadCreator = ({ listingId, start, end, timeZone, options }, thunkAPI) => {
  const { dispatch, getState } = thunkAPI;
  const { extraQueryParams = null, useFetchTimeSlotsForDate = false } = options || {};

  // The maximum pagination page size for timeSlots is 500
  const extraParams = extraQueryParams || {
    perPage: 500,
    page: 1,
  };

  // For small time units, we fetch the data per date.
  // This is to avoid fetching too much data (with 15 minute intervals, there can be 24*4*31 = 2928 time slots)
  if (useFetchTimeSlotsForDate) {
    const dateId = stringifyDateToISO8601(start, timeZone);
    const dateData = getState().ListingPage.timeSlotsForDate[dateId];
    const minuteAgo = new Date().getTime() - MINUTE_IN_MS;
    const hasRecentlyFetchedData = dateData?.fetchedAt > minuteAgo;
    if (hasRecentlyFetchedData) {
      return Promise.resolve(dateData?.timeSlots || []);
    }

    return dispatch(timeSlotsRequest({ listingId, start, end, ...extraParams }))
      .then(response => {
        return response.payload;
      })
      .catch(e => {
        return [];
      });
  } else {
    return dispatch(timeSlotsRequest({ listingId, start, end, ...extraParams }))
      .then(response => {
        return response.payload;
      })
      .catch(e => {
        return [];
      });
  }
};

export const fetchTimeSlotsThunk = createAsyncThunk(
  'ListingPage/fetchTimeSlots',
  fetchTimeSlotsPayloadCreator
);
// Backward compatible wrapper for the thunk
export const fetchTimeSlots = (listingId, start, end, timeZone, options) => (
  dispatch,
  getState,
  sdk
) => {
  return dispatch(fetchTimeSlotsThunk({ listingId, start, end, timeZone, options })).unwrap();
};

//////////////////
// Send Inquiry //
//////////////////
const sendInquiryPayloadCreator = (
  { listing, message },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  const processAlias = listing?.attributes?.publicData?.transactionProcessAlias;
  if (!processAlias) {
    const error = new Error('No transaction process attached to listing');
    log.error(error, 'listing-process-missing', {
      listingId: listing?.id?.uuid,
    });
    return rejectWithValue(storableError(error));
  }

  const listingId = listing?.id;
  const [processName, alias] = processAlias.split('/');

  // Negotiation process does not support inquiry for customer role on OFFER flog (only request for quote is supported)
  const isNegotiationProcess = isNegotiationProcessAlias(processAlias);
  const unitType = listing?.attributes?.publicData?.unitType || '';
  if (isNegotiationProcess && unitType === OFFER) {
    return rejectWithValue(
      storableError(
        new Error(
          'Negotiation process with unit type OFFER does not support inquiry for customer role'
        )
      )
    );
  }

  const transitions = getProcess(processName)?.transitions;

  const bodyParams = {
    transition: transitions.INQUIRE,
    processAlias,
    params: { listingId, protectedData: message ? { inquiryMessage: message } : {} },
  };
  return sdk.transactions
    .initiate(bodyParams)
    .then(response => {
      const transactionId = response.data.data.id;
      dispatch(setCurrentUserHasOrders());
      return transactionId;
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const sendInquiryThunk = createAsyncThunk(
  'ListingPage/sendInquiry',
  sendInquiryPayloadCreator
);
// Backward compatible wrapper for the thunk
export const sendInquiry = (listing, message) => (dispatch, getState, sdk) => {
  return dispatch(sendInquiryThunk({ listing, message })).unwrap();
};

/////////////////////////////
// Fetch Existing Chat TX   //
/////////////////////////////
export const fetchExistingChatTransactionThunk = createAsyncThunk(
  'ListingPage/fetchExistingChatTransaction',
  async ({ listingId }, { dispatch, rejectWithValue, extra: sdk }) => {
    try {
      const response = await sdk.transactions.query({
        perPage: 100,
        include: ['listing'],
        'fields.transaction': [
          'lastTransition',
          'lastTransitionedAt',
          'createdAt',
          'protectedData',
          'processName',
          'metadata',
          'payinTotal',
          'lineItems',
          'transitions',
        ],
        'fields.listing': ['title'],
      });
      const txs = response.data.data;
      const candidates = txs.filter(tx => {
        const listingRef = tx.relationships?.listing?.data;
        return listingRef && listingRef.id.uuid === listingId.uuid;
      });
      if (candidates.length === 0) return null;

      // Prefer the most recent non-terminal tx so the listing-page chat doesn't
      // attach to a closed tx (review-complete, cancelled, expired, rejected)
      // while an active one exists. Detect terminals via the process's own
      // isCompleted / isRefunded helpers when available, plus a state-name
      // catch-all that works across default-purchase / default-negotiation /
      // chat-offer-buy / default-booking.
      const TERMINAL_STATE_NAMES = new Set([
        // chat-offer-buy / default-negotiation terminals
        'offer-rejected',
        'payment-expired',
        'canceled',
        'cancelled',
        'reviewed',
        // default-purchase terminals
        'delivered',
        'received',
        'reviewed-by-customer',
        'reviewed-by-provider',
        'completed',
      ]);
      const isTerminal = tx => {
        try {
          const procName = resolveLatestProcessName(tx.attributes.processName);
          const process = procName ? getProcess(procName) : null;
          const lastTransition = tx.attributes.lastTransition;
          if (process?.isCompleted && process.isCompleted(lastTransition)) return true;
          if (process?.isRefunded && process.isRefunded(lastTransition)) return true;
          const stateRaw =
            process && typeof process.getState === 'function'
              ? process.getState({ attributes: { lastTransition } })
              : null;
          const state = typeof stateRaw === 'string'
            ? (stateRaw.startsWith('state/') ? stateRaw.slice('state/'.length) : stateRaw)
            : null;
          if (state && TERMINAL_STATE_NAMES.has(state)) return true;
          return false;
        } catch (_e) {
          return false;
        }
      };
      const byRecency = (a, b) => {
        const aAt = a.attributes.lastTransitionedAt || a.attributes.createdAt || 0;
        const bAt = b.attributes.lastTransitionedAt || b.attributes.createdAt || 0;
        return new Date(bAt).getTime() - new Date(aAt).getTime();
      };
      const sorted = [...candidates].sort(byRecency);
      const active = sorted.find(tx => !isTerminal(tx));
      if (!active) return null;
      const match = active;

      // Buyer-side pickup code: when the active tx is in a pickup-eligible
      // state, fan out the read so the buyer card on the listing page can
      // read straight from Redux.
      if (PICKUP_CODE_LAST_TRANSITIONS.includes(match.attributes.lastTransition)) {
        dispatch(fetchPickupCodeThunk({ transactionId: match.id.uuid }));
      }

      return {
        transactionId: match.id,
        lastTransition: match.attributes.lastTransition,
        lastTransitionedAt: match.attributes.lastTransitionedAt || null,
        processName: match.attributes.processName || null,
        protectedData: match.attributes.protectedData || {},
        createdAt: match.attributes.createdAt || null,
        metadata: match.attributes.metadata || {},
        payinTotal: match.attributes.payinTotal || null,
        lineItems: match.attributes.lineItems || [],
        transitions: match.attributes.transitions || [],
      };
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);
export const fetchExistingChatTransaction = listingId => (dispatch, getState, sdk) =>
  dispatch(fetchExistingChatTransactionThunk({ listingId })).unwrap();

/////////////////////////////
// Fetch Chat Messages      //
/////////////////////////////
export const fetchChatMessagesThunk = createAsyncThunk(
  'ListingPage/fetchChatMessages',
  async ({ transactionId }, { rejectWithValue, extra: sdk }) => {
    try {
      const response = await sdk.messages.query({
        transaction_id: transactionId,
        include: ['sender', 'sender.profileImage'],
        'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
        perPage: 100,
        page: 1,
      });
      return denormalisedResponseEntities(response);
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);
export const fetchChatMessages = transactionId => (dispatch, getState, sdk) =>
  dispatch(fetchChatMessagesThunk({ transactionId })).unwrap();

/////////////////////////////
// Send Chat Message        //
/////////////////////////////
export const sendChatMessageThunk = createAsyncThunk(
  'ListingPage/sendChatMessage',
  async ({ transactionId, content }, { rejectWithValue, extra: sdk }) => {
    try {
      await sdk.messages.send({ transactionId, content });
      const response = await sdk.messages.query({
        transaction_id: transactionId,
        include: ['sender', 'sender.profileImage'],
        'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
        perPage: 100,
        page: 1,
      });
      return denormalisedResponseEntities(response);
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);
export const sendChatMessage = (transactionId, content) => (dispatch, getState, sdk) =>
  dispatch(sendChatMessageThunk({ transactionId, content })).unwrap();

/////////////////////////////
// Fetch Seller Buyer Chats //
/////////////////////////////
export const fetchSellerBuyerChatsThunk = createAsyncThunk(
  'ListingPage/fetchSellerBuyerChats',
  async ({ listingId }, { rejectWithValue, extra: sdk }) => {
    try {
      const response = await sdk.transactions.query({
        only: 'sale',
        perPage: 50,
        include: ['customer', 'customer.profileImage', 'listing'],
        'fields.transaction': [
          'lastTransition',
          'lastTransitionedAt',
          'createdAt',
          'processName',
          'protectedData',
        ],
        'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
        'fields.listing': ['title'],
      });
      const txs = response.data.data;
      const included = response.data.included || [];

      // Same terminal-state filter used by fetchExistingChatTransactionThunk
      // so closed sales (offer-rejected, payment-expired, canceled, reviewed,
      // delivered, received, completed, default-purchase reviewed-by-*)
      // don't clutter the seller's "Active buyer chats" sidebar.
      const TERMINAL_STATE_NAMES = new Set([
        'offer-rejected',
        'payment-expired',
        'canceled',
        'cancelled',
        'reviewed',
        'reviewed-by-customer',
        'reviewed-by-provider',
        'delivered',
        'received',
        'completed',
      ]);
      const isTerminal = tx => {
        try {
          const procName = resolveLatestProcessName(tx.attributes.processName);
          const process = procName ? getProcess(procName) : null;
          const lastTransition = tx.attributes.lastTransition;
          if (process?.isCompleted && process.isCompleted(lastTransition)) return true;
          if (process?.isRefunded && process.isRefunded(lastTransition)) return true;
          const stateRaw =
            process && typeof process.getState === 'function'
              ? process.getState({ attributes: { lastTransition } })
              : null;
          const state = typeof stateRaw === 'string'
            ? (stateRaw.startsWith('state/') ? stateRaw.slice('state/'.length) : stateRaw)
            : null;
          if (state && TERMINAL_STATE_NAMES.has(state)) return true;
          return false;
        } catch (_e) {
          return false;
        }
      };
      const byRecency = (a, b) => {
        const aAt = a.attributes.lastTransitionedAt || a.attributes.createdAt || 0;
        const bAt = b.attributes.lastTransitionedAt || b.attributes.createdAt || 0;
        return new Date(bAt).getTime() - new Date(aAt).getTime();
      };
      const matches = txs
        .filter(tx => {
          const listingRef = tx.relationships?.listing?.data;
          return listingRef && listingRef.id.uuid === listingId.uuid;
        })
        .filter(tx => !isTerminal(tx))
        .sort(byRecency)
        .slice(0, 10);

      const chats = await Promise.all(
        matches.map(async tx => {
          const customerRef = tx.relationships?.customer?.data;
          const customer = customerRef
            ? included.find(e => e.type === 'user' && e.id.uuid === customerRef.id.uuid)
            : null;
          const displayName = customer?.attributes?.profile?.displayName || 'Buyer';
          const abbreviatedName = customer?.attributes?.profile?.abbreviatedName || '?';

          // Use the central transactionMessages helper so this surface
          // resolves "last message" the same way the inbox card + listing
          // chat panel do (sdk.messages first, then chat-offer-buy
          // customer/provider default-message fields on protectedData).
          let messages = [];
          try {
            const msgResponse = await sdk.messages.query({
              transaction_id: tx.id,
              perPage: 100,
              page: 1,
            });
            messages = msgResponse.data.data || [];
          } catch (_e) {
            // ignore
          }
          const last = getLastCounterpartyMessageFromParts({
            messages,
            protectedData: tx.attributes.protectedData,
            // Seller is viewing this list - the buyer is the counterparty.
            viewerRole: 'provider',
            otherUserId: customerRef?.id?.uuid,
          });
          const lastMessageText = last?.content || null;
          const lastMessageFrom = last
            ? last.isOwn
              ? 'provider'
              : 'customer'
            : null;

          return {
            transactionId: tx.id,
            lastTransition: tx.attributes.lastTransition,
            customerId: customerRef?.id,
            customerDisplayName: displayName,
            customerAbbreviatedName: abbreviatedName,
            lastMessageText,
            lastMessageFrom,
          };
        })
      );

      return chats;
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);
export const fetchSellerBuyerChats = listingId => (dispatch, getState, sdk) =>
  dispatch(fetchSellerBuyerChatsThunk({ listingId })).unwrap();

// Helper function for loadData call.
// Note: listing could be ownListing entity too
const fetchMonthlyTimeSlots = (dispatch, listing) => {
  const hasWindow = typeof window !== 'undefined';
  const { availabilityPlan, publicData } = listing?.attributes || {};
  const tz = availabilityPlan?.timezone;

  // Fetch time-zones on client side only.
  if (hasWindow && listing.id && !!tz) {
    const { unitType, priceVariants, startTimeInterval } = publicData || {};
    const now = new Date();
    const startOfToday = getStartOf(now, 'day', tz);
    const isFixed = unitType === 'fixed';

    const timeUnit = startTimeInterval
      ? bookingTimeUnits[startTimeInterval]?.timeUnit
      : unitType === 'hour'
      ? 'hour'
      : 'day';
    const nextBoundary = findNextBoundary(now, 1, timeUnit, tz);

    const nextMonth = getStartOf(nextBoundary, 'month', tz, 1, 'months');
    const nextAfterNextMonth = getStartOf(nextMonth, 'month', tz, 1, 'months');

    const variants = priceVariants || [];
    const bookingLengthInMinutes = variants.reduce((min, priceVariant) => {
      return Math.min(min, priceVariant.bookingLengthInMinutes);
    }, Number.MAX_SAFE_INTEGER);

    const nextMonthEnd = isFixed
      ? getStartOf(nextMonth, 'minute', tz, bookingLengthInMinutes, 'minutes')
      : nextMonth;
    const followingMonthEnd = isFixed
      ? getStartOf(nextAfterNextMonth, 'minute', tz, bookingLengthInMinutes, 'minutes')
      : nextAfterNextMonth;

    const minDurationStartingInInterval = isFixed ? bookingLengthInMinutes : 60;

    const options = intervalAlign => {
      return ['fixed', 'hour'].includes(unitType)
        ? {
            extraQueryParams: {
              intervalDuration: 'P1D',
              intervalAlign,
              maxPerInterval: 1,
              minDurationStartingInInterval,
              perPage: 31,
              page: 1,
            },
          }
        : null;
    };

    return Promise.all([
      dispatch(fetchTimeSlots(listing.id, nextBoundary, nextMonthEnd, tz, options(startOfToday))),
      dispatch(fetchTimeSlots(listing.id, nextMonth, followingMonthEnd, tz, options(nextMonth))),
    ]);
  }

  // By default return an empty array
  return Promise.all([]);
};

//////////////////////////////////
// Fetch Transaction Line Items //
//////////////////////////////////
const fetchTransactionLineItemsPayloadCreator = (
  { orderData, listingId, isOwnListing },
  { rejectWithValue }
) => {
  return transactionLineItems({ orderData, listingId, isOwnListing })
    .then(response => {
      return response.data;
    })
    .catch(e => {
      log.error(e, 'fetching-line-items-failed', {
        listingId: listingId.uuid,
        orderData,
        statusText: e.statusText,
      });
      return rejectWithValue(storableError(e));
    });
};

export const fetchTransactionLineItemsThunk = createAsyncThunk(
  'ListingPage/fetchTransactionLineItems',
  fetchTransactionLineItemsPayloadCreator
);
// Backward compatible wrapper for the thunk
export const fetchTransactionLineItems = ({ orderData, listingId, isOwnListing }) => dispatch => {
  return dispatch(fetchTransactionLineItemsThunk({ orderData, listingId, isOwnListing })).unwrap();
};

// ================ Slice ================ //

const initialState = {
  id: null,
  showListingError: null,
  reviews: [],
  fetchReviewsError: null,
  monthlyTimeSlots: {
    // '2022-03': {
    //   timeSlots: [],
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  timeSlotsForDate: {
    // For small time units, we fetch monthly time slots with sparse mode for calendar view
    // and when the user clicks on a day, we make a full time slot query. This is for that purpose.
    // '2025-02-03': {
    //   timeSlots: [],
    //   fetchedAt: 1738569600000,
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  lineItems: null,
  fetchLineItemsInProgress: false,
  fetchLineItemsError: null,
  sendInquiryInProgress: false,
  sendInquiryError: null,
  inquiryModalOpenForListingId: null,
  // Inline chat
  existingTransactionId: null,
  existingTransactionLastTransition: null,
  existingTransactionProcessName: null,
  existingTransactionProtectedData: null,
  existingTransactionCreatedAt: null,
  existingTransactionLastTransitionedAt: null,
  existingTransactionMetadata: null,
  existingTransactionPayinTotal: null,
  existingTransactionLineItems: null,
  existingTransactionTransitions: null,
  fetchExistingTransactionInProgress: false,
  chatMessages: [],
  fetchChatMessagesInProgress: false,
  sendChatMessageInProgress: false,
  sendChatMessageError: null,
  // Seller buyer chats
  buyerChats: [],
  fetchBuyerChatsInProgress: false,
};

const listingPageSlice = createSlice({
  name: 'ListingPage',
  initialState,
  reducers: {
    setInitialValues: (state, action) => {
      return { ...initialState, ...action.payload };
    },
  },
  extraReducers: builder => {
    builder
      .addCase(showListingThunk.pending, (state, action) => {
        state.id = action.meta.arg.listingId;
        state.showListingError = null;
      })
      .addCase(showListingThunk.fulfilled, (state, action) => {
        // Data is handled by addMarketplaceEntities in the thunk
        state.id = action.payload.data.data.id;
      })
      .addCase(showListingThunk.rejected, (state, action) => {
        state.showListingError = action.payload;
      })
      .addCase(fetchReviewsThunk.pending, state => {
        state.fetchReviewsError = null;
      })
      .addCase(fetchReviewsThunk.fulfilled, (state, action) => {
        state.reviews = action.payload;
      })
      .addCase(fetchReviewsThunk.rejected, (state, action) => {
        state.fetchReviewsError = action.payload;
      })
      .addCase(fetchSellerReviewsThunk.pending, state => {
        state.fetchReviewsError = null;
      })
      .addCase(fetchSellerReviewsThunk.fulfilled, (state, action) => {
        state.reviews = action.payload;
      })
      .addCase(fetchSellerReviewsThunk.rejected, (state, action) => {
        state.fetchReviewsError = action.payload;
      })
      .addCase(fetchTimeSlotsThunk.pending, (state, action) => {
        const { options, start, timeZone } = action.meta.arg;
        const { useFetchTimeSlotsForDate = false } = options || {};

        if (useFetchTimeSlotsForDate) {
          const dateId = stringifyDateToISO8601(start, timeZone);
          state.timeSlotsForDate = removeOutdatedDateData(state.timeSlotsForDate);
          if (!state.timeSlotsForDate[dateId]) {
            state.timeSlotsForDate[dateId] = {};
          }
          state.timeSlotsForDate[dateId].fetchTimeSlotsError = null;
          state.timeSlotsForDate[dateId].fetchedAt = null;
          state.timeSlotsForDate[dateId].fetchTimeSlotsInProgress = true;
          state.timeSlotsForDate[dateId].timeSlots = [];
        } else {
          const monthId = monthIdString(start, timeZone);
          if (!state.monthlyTimeSlots[monthId]) {
            state.monthlyTimeSlots[monthId] = {};
          }
          state.monthlyTimeSlots[monthId].fetchTimeSlotsError = null;
          state.monthlyTimeSlots[monthId].fetchTimeSlotsInProgress = true;
        }
      })
      .addCase(fetchTimeSlotsThunk.fulfilled, (state, action) => {
        const { options, start, timeZone } = action.meta.arg;
        const { useFetchTimeSlotsForDate = false } = options || {};

        if (useFetchTimeSlotsForDate) {
          const dateId = stringifyDateToISO8601(start, timeZone);
          if (!state.timeSlotsForDate[dateId]) {
            state.timeSlotsForDate[dateId] = {};
          }
          state.timeSlotsForDate[dateId].fetchTimeSlotsInProgress = false;
          state.timeSlotsForDate[dateId].fetchedAt = new Date().getTime();
          state.timeSlotsForDate[dateId].timeSlots = action.payload;
        } else {
          const monthId = monthIdString(start, timeZone);
          if (!state.monthlyTimeSlots[monthId]) {
            state.monthlyTimeSlots[monthId] = {};
          }
          state.monthlyTimeSlots[monthId].fetchTimeSlotsInProgress = false;
          state.monthlyTimeSlots[monthId].timeSlots = action.payload;
        }
      })
      .addCase(fetchTimeSlotsThunk.rejected, (state, action) => {
        const { options, start, timeZone } = action.meta.arg;
        const { useFetchTimeSlotsForDate = false } = options || {};

        if (useFetchTimeSlotsForDate) {
          const dateId = stringifyDateToISO8601(start, timeZone);
          if (!state.timeSlotsForDate[dateId]) {
            state.timeSlotsForDate[dateId] = {};
          }
          state.timeSlotsForDate[dateId].fetchTimeSlotsInProgress = false;
          state.timeSlotsForDate[dateId].fetchTimeSlotsError = action.payload;
        } else {
          const monthId = monthIdString(start, timeZone);
          if (!state.monthlyTimeSlots[monthId]) {
            state.monthlyTimeSlots[monthId] = {};
          }
          state.monthlyTimeSlots[monthId].fetchTimeSlotsInProgress = false;
          state.monthlyTimeSlots[monthId].fetchTimeSlotsError = action.payload;
        }
      })
      .addCase(sendInquiryThunk.pending, state => {
        state.sendInquiryInProgress = true;
        state.sendInquiryError = null;
      })
      .addCase(sendInquiryThunk.fulfilled, state => {
        state.sendInquiryInProgress = false;
        state.inquiryModalOpenForListingId = null;
      })
      .addCase(sendInquiryThunk.rejected, (state, action) => {
        state.sendInquiryInProgress = false;
        state.sendInquiryError = action.payload;
      })
      .addCase(fetchExistingChatTransactionThunk.pending, state => {
        state.fetchExistingTransactionInProgress = true;
      })
      .addCase(fetchExistingChatTransactionThunk.fulfilled, (state, action) => {
        state.fetchExistingTransactionInProgress = false;
        state.existingTransactionId = action.payload?.transactionId || null;
        state.existingTransactionLastTransition = action.payload?.lastTransition || null;
        state.existingTransactionProcessName = action.payload?.processName || null;
        state.existingTransactionProtectedData = action.payload?.protectedData || null;
        state.existingTransactionCreatedAt = action.payload?.createdAt || null;
        state.existingTransactionLastTransitionedAt = action.payload?.lastTransitionedAt || null;
        state.existingTransactionMetadata = action.payload?.metadata || null;
        state.existingTransactionPayinTotal = action.payload?.payinTotal || null;
        state.existingTransactionLineItems = action.payload?.lineItems || null;
        state.existingTransactionTransitions = action.payload?.transitions || null;
      })
      .addCase(fetchExistingChatTransactionThunk.rejected, state => {
        state.fetchExistingTransactionInProgress = false;
      })
      .addCase(fetchChatMessagesThunk.pending, state => {
        state.fetchChatMessagesInProgress = true;
      })
      .addCase(fetchChatMessagesThunk.fulfilled, (state, action) => {
        state.fetchChatMessagesInProgress = false;
        state.chatMessages = action.payload;
      })
      .addCase(fetchChatMessagesThunk.rejected, state => {
        state.fetchChatMessagesInProgress = false;
      })
      .addCase(sendChatMessageThunk.pending, state => {
        state.sendChatMessageInProgress = true;
        state.sendChatMessageError = null;
      })
      .addCase(sendChatMessageThunk.fulfilled, (state, action) => {
        state.sendChatMessageInProgress = false;
        state.chatMessages = action.payload;
      })
      .addCase(sendChatMessageThunk.rejected, (state, action) => {
        state.sendChatMessageInProgress = false;
        state.sendChatMessageError = action.payload;
      })
      .addCase(fetchSellerBuyerChatsThunk.pending, state => {
        state.fetchBuyerChatsInProgress = true;
      })
      .addCase(fetchSellerBuyerChatsThunk.fulfilled, (state, action) => {
        state.fetchBuyerChatsInProgress = false;
        state.buyerChats = action.payload;
      })
      .addCase(fetchSellerBuyerChatsThunk.rejected, state => {
        state.fetchBuyerChatsInProgress = false;
      })
      .addCase(fetchTransactionLineItemsThunk.pending, state => {
        state.fetchLineItemsInProgress = true;
        state.fetchLineItemsError = null;
      })
      .addCase(fetchTransactionLineItemsThunk.fulfilled, (state, action) => {
        state.fetchLineItemsInProgress = false;
        state.lineItems = action.payload;
      })
      .addCase(fetchTransactionLineItemsThunk.rejected, (state, action) => {
        state.fetchLineItemsInProgress = false;
        state.fetchLineItemsError = action.payload;
      });
  },
});

export const { setInitialValues } = listingPageSlice.actions;

export default listingPageSlice.reducer;

// ================ Load data ================ //

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const listingId = new UUID(params.id);
  const state = getState();
  const currentUser = state.user?.currentUser;
  const inquiryModalOpenForListingId =
    isUserAuthorized(currentUser) && hasPermissionToInitiateTransactions(currentUser)
      ? state.ListingPage.inquiryModalOpenForListingId
      : null;

  // Clear old line-items
  dispatch(setInitialValues({ lineItems: null, inquiryModalOpenForListingId }));

  const ownListingVariants = [LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT];
  if (ownListingVariants.includes(params.variant)) {
    return dispatch(showListing(listingId, config, true));
  }

  // In private marketplace mode, this page won't fetch data if the user is unauthorized
  const isAuthorized = currentUser && isUserAuthorized(currentUser);
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const canFetchData = !isPrivateMarketplace || (isPrivateMarketplace && isAuthorized);
  if (!canFetchData) {
    return Promise.resolve();
  }

  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);

  if (hasNoViewingRights) {
    return dispatch(showListing(listingId, config, true));
  }

  // Fetch listing first, then use its author ID to fetch all seller reviews
  return dispatch(showListing(listingId, config)).then(listingResponse => {
    const listing = listingResponse?.data?.data;
    const transactionProcessAlias = listing?.attributes?.publicData?.transactionProcessAlias || '';
    if (isBookingProcessAlias(transactionProcessAlias)) {
      fetchMonthlyTimeSlots(dispatch, listing);
    }

    // Fetch all reviews for this seller (not just this listing)
    const authorId = listing?.relationships?.author?.data?.id?.uuid;
    if (authorId) {
      dispatch(fetchSellerReviews(authorId));
    }

    const callerSharetribeId = currentUser?.id?.uuid;
    if (authorId && callerSharetribeId && authorId === callerSharetribeId) {
      dispatch(boostApi.endpoints.fetchBoosts.initiate({ page: 1, limit: 50 }));
    }

    return listingResponse;
  });
};
